import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDesignStore } from '@/stores';
import { usePublishStore } from '@/stores/publishStore';

/** The states the header can show; derived from the design store only. */
export type SaveStatusKind = 'error' | 'saving' | 'unsaved' | 'saved';

interface SaveStatusInputs {
  syncError: string | null;
  isSaving: boolean;
  hasUnsavedChanges: boolean;
  lastSavedAt: Date | null;
}

/**
 * Pick the one state worth showing. A failure outranks everything: a save
 * that is running again or new edits do not hide it until the store clears
 * `syncError` (every save path resets it when it starts). Before the first
 * successful save there is nothing to report.
 */
export function resolveSaveStatus({
  syncError,
  isSaving,
  hasUnsavedChanges,
  lastSavedAt,
}: SaveStatusInputs): SaveStatusKind | null {
  if (syncError) return 'error';
  if (isSaving) return 'saving';
  if (hasUnsavedChanges) return 'unsaved';
  if (lastSavedAt) return 'saved';
  return null;
}

/**
 * Save state of the open design in the creator header.
 *
 * Reads only the store's existing save fields; no state of its own. Both
 * live regions stay mounted (empty when idle) so assistive technology has
 * registered them before the first change is announced: routine states go
 * through the polite region, a failed save through the assertive one so it
 * interrupts. The retry button calls the store's `saveDesign`, which
 * coalesces onto a running save and reports a repeat failure via
 * `syncError` again.
 *
 * While a publish runs the retry is withheld, as are autosave, the step bar
 * and leaving: the publish owns the save sequence, and a save started from
 * here could land after the status turned ACTIVE. The error text stays.
 */
export function SaveStatus() {
  const { t } = useTranslation();
  const syncError = useDesignStore((state) => state.syncError);
  const isSaving = useDesignStore((state) => state.isSaving);
  const hasUnsavedChanges = useDesignStore((state) => state.hasUnsavedChanges);
  const lastSavedAt = useDesignStore((state) => state.lastSavedAt);
  const isPublishing = usePublishStore((state) => state.isPublishing);

  const status = resolveSaveStatus({
    syncError,
    isSaving,
    hasUnsavedChanges,
    lastSavedAt,
  });

  const handleRetry = () => {
    void useDesignStore.getState().saveDesign();
  };

  return (
    <div className="flex items-center min-w-[7.5rem] font-mono text-[11px] tracking-[0.04em] whitespace-nowrap">
      <span aria-live="polite" className="text-[var(--mute)]">
        {status && status !== 'error'
          ? t(`creator.header.saveStatus.${status}`)
          : null}
      </span>
      <span
        aria-live="assertive"
        className="flex items-center gap-1.5 text-[var(--danger)]"
      >
        {status === 'error' && (
          <>
            <AlertCircle className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
            {t('creator.header.saveStatus.error')}
            {!isPublishing && (
              <button
                type="button"
                onClick={handleRetry}
                className="underline underline-offset-2 decoration-[var(--line)] hover:decoration-current text-[var(--ink)] transition-colors"
              >
                {t('creator.header.saveStatus.retry')}
              </button>
            )}
          </>
        )}
      </span>
    </div>
  );
}
